import { Cancel } from '@mui/icons-material';
import { useAudio } from '@rhg/hooks/use-audio';
import playerMessages from '@rhg/lang/messages/player';
import { Box, IconButton, Slide, Typography } from '@rhg/libs/material';
import { PauseFilled, PlayFilled } from '@rhg/libs/material/icons';
import { styleMaxLines, sxAppend } from '@rhg/libs/material/sx';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AudioTimeline } from '../timeline';
import {
    AudioMinimalControlsProps,
    AudioPlayerControls,
    AudioTimelineVariant,
} from '../types';

const styleAudioControlsContainer = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
};

const styleFloatingContainer = {
    backgroundColor: 'background.paper',
};

const styleAudioControlsMain = {
    height: 'calc(100% - 5px)',
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
};

const styleAudioControlsLeft = {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    position: 'relative',
};

const styleButton = { mx: 4 };

const styleSliderContainer = { width: '100%' };

export function AudioMinimalControls({
    onStop,
    title,
    subtitle,
    image,
    isFloating = false,
    sx,
    controlsSx,
    controls,
    areControlsHidden,
    onControlClick,
}: AudioMinimalControlsProps) {
    const { formatMessage } = useIntl();

    const isSoundScape = useMemo(
        () => controls === AudioPlayerControls.Soundscape,
        [controls]
    );

    const {
        controls: { timingState, togglePlay, isPaused },
    } = useAudio();

    const handleTogglePlay = () => {
        if (onControlClick) {
            // The reverse of the current state, prior to calling togglePlay
            onControlClick(isPaused ? 'play' : 'pause');
        }

        togglePlay();
    };

    return (
        <Slide
            direction="up"
            in={!areControlsHidden}
            mountOnEnter
            unmountOnExit
        >
            <Box sx={sx}>
                <Box
                    sx={sxAppend(
                        styleAudioControlsContainer,
                        isFloating && styleFloatingContainer,
                        controlsSx
                    )}
                >
                    <Box sx={styleAudioControlsMain}>
                        <Box sx={styleAudioControlsLeft}>
                            {image}
                            <IconButton
                                size="medium"
                                trackButton={
                                    timingState.isPaused
                                        ? 'AudioPlayerPlay'
                                        : 'AudioPlayerPause'
                                }
                                onClick={handleTogglePlay}
                                aria-label={
                                    timingState.isPaused
                                        ? formatMessage(
                                              playerMessages['player.play']
                                          )
                                        : formatMessage(
                                              playerMessages['player.pause']
                                          )
                                }
                                sx={styleButton}
                            >
                                {timingState.isPaused ? (
                                    <PlayFilled color="primary" />
                                ) : (
                                    <PauseFilled color="primary" />
                                )}
                            </IconButton>
                            {(title || subtitle) && (
                                <Box>
                                    <Typography
                                        variant="body1Demi"
                                        component="p"
                                        color="text.body"
                                        sx={styleMaxLines(1)}
                                    >
                                        {title}
                                    </Typography>
                                    <Typography
                                        variant="subtitle2"
                                        component="p"
                                        color="text.sub"
                                        sx={styleMaxLines(1)}
                                    >
                                        {subtitle}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <IconButton
                            trackButton="AudioPlayerStop"
                            onClick={onStop}
                            sx={styleButton}
                            aria-label={formatMessage(
                                playerMessages['player.stop']
                            )}
                        >
                            <Cancel color="primary" />
                        </IconButton>
                    </Box>
                    {!isSoundScape && (
                        <Box sx={styleSliderContainer}>
                            <AudioTimeline
                                variant={AudioTimelineVariant.Minimal}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </Slide>
    );
}
